<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :key="tableKey"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :enableMultiSelect="false"
            :enableDragDrop="false"
            @page-changed="handlePageChange"
            :perPage="itemsPerPage"
            @per-page-changed="handlePerPageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>
                    </div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="navigateToCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('blog.add_blog') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Title -->
            <template #column-title="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.title }}
                </span>
            </template>

            <!-- Column: Image -->
            <template #column-image="{ item }">
                <div class="ppv2-flex-shrink-0">
                    <img
                        v-if="item.image"
                        :src="item.image"
                        alt="Image"
                        class="ppv2-w-10 ppv2-h-10 ppv2-object-cover ppv2-rounded-lg"
                    />
                    <div
                        v-else
                        class="ppv2-w-10 ppv2-h-10 ppv2-bg-gray-200 ppv2-rounded-lg ppv2-flex ppv2-items-center ppv2-justify-center"
                    ></div>
                </div>
            </template>

            <!-- Column: Blogger -->
            <!-- <template #column-blogger="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.blogger || '-' }}
                </span>
            </template> -->

            <!-- Column: Categories -->
            <template #column-categories="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.categories || '-' }}
                </span>
            </template>

            <!-- Column: Publish At -->
            <template #column-publish_at="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ formatDate(item.publish_at) }}
                </span>
            </template>

            <!-- Column: Status (Is Publish) -->
            <template #column-is_publish="{ item }">
                <button
                    @click="togglePublish(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-1 ppv2-transition-all"
                    :class="
                        item.is_publish
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="item.is_publish ? 'ppv2-bg-[#206A64] ppv2-justify-end' : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'"
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.is_publish ? $t('blog.published') : $t('blog.unpublished') }}
                    </span>
                </button>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div
                    :style="{ fontSize: normalizeFontSize(14) }"
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                >
                    <button
                        @click="openEditTab(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular ppv2-text-black">
                    {{ $t('customers.selectedItems', { count: selectedCount, plural: selectedCount > 1 ? 's' : '' }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, Trash2, SquarePen } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import Search from '../../Experience/components/Search.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import DataTable from '../../../Shared/DataTable.vue'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    name: 'BlogTable',
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    components: {
        Plus,
        SquarePen,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('blogs_items_per_page', 10),
            selectedItems: [],
            selectAll: false,
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'image', label: 'Image', sortable: false, headerClass: 'ppv2-text-center' },
                { key: 'title', label: this.$t('blog.columns.title'), sortable: true, headerClass: 'ppv2-text-left' },
                // { key: 'blogger', label: this.$t('blog.columns.blogger'), sortable: true, headerClass: 'ppv2-text-left' },
                { key: 'categories', label: this.$t('blog.columns.categories'), sortable: false, headerClass: 'ppv2-text-left' },
                { key: 'publish_at', label: this.$t('blog.columns.publish_at'), sortable: true, headerClass: 'ppv2-text-center' },
                {
                    key: 'is_publish',
                    label: this.$t('blog.columns.status'),
                    sortable: true,
                    headerClass: 'ppv2-text-center',
                },
                { key: 'actions', label: this.$t('blog.columns.actions'), sortable: false, headerClass: 'ppv2-text-center' },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('blog.delete_title'),
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            items: {
                data: [],
                total: 0,
                last_page: 1,
            },
            tableKey: 0,
            orderBy: 'id',
            orderDirection: 'desc',
        }
    },
    computed: {
        paginatedItems() {
            return this.items.data || []
        },
        totalPages() {
            return this.items.last_page || 1
        },
    },
    mounted() {
        this.fetchItems()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        navigateToCreate() {
            window.location.href = '/back-office/v3/blogs/create'
        },
        formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('fr-FR')
        },
        async togglePublish(item) {
            const newStatus = !item.is_publish
            const action = newStatus ? 'publish' : 'unpublish'
            try {
                const response = await axios.post(
                    `/api/v3/blog/${action}`,
                    { id: item.id },
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                item.is_publish = newStatus
                if (newStatus) {
                    item.publish_at = new Date().toISOString()
                }
                if (response.status === 200) {
                    this.$toast.success(newStatus ? this.$t('blog.publish_success') : this.$t('blog.unpublish_success'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchItems()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('blogs_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchItems()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchItems()
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchItems()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.paginatedItems.length
        },
        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedItems = this.paginatedItems.map((item) => item.id)
            } else {
                this.selectedItems = []
            }
        },
        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('blog.delete_description_multiple')
            this.showDeletePopup = true
        },
        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('blog.delete_description_single')
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                let ids = []
                if (this.deleteAction === 'single') {
                    ids = [this.itemToDelete]
                } else {
                    ids = this.selectedItems
                }

                await axios.post(`/api/v3/blog/delete`, { ids: ids }, { headers: { Authorization: `Bearer ${this.token}` } })

                this.fetchItems()
                this.selectedItems = []
                this.selectAll = false
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Delete error:', error)
            }
            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        openEditTab(item) {
            window.location.href = `/back-office/v3/blogs/${item.id}/edit`
        },
        async fetchItems() {
            this.isLoading = true
            this.error = null
            try {
                let url = `/api/v3/blog?currentPage=${this.currentPage}&itemsPerPage=${this.itemsPerPage}&search=${this.searchQuery}&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}`
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${this.token}` } })
                const data = response.data
                this.items = {
                    data: data.data || [],
                    total: data.total || 0,
                    last_page: data.last_page || 1,
                }
            } catch (err) {
                this.error = err.message || this.$t('blog.fetch_error')
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedItems.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
